import { PAYMENT_SYSTEM_LABELS, type PaymentSystem } from '../types/payment';
import { formatPaymentAmount, type PaymentsReceivedRow } from './paymentsReceivedDashboard';

export type PaymentsReceivedExportFormat = 'pdf' | 'csv' | 'json';

export interface PaymentsReceivedExportRow {
  nome: string;
  email: string;
  gateway: string;
  valor: string;
}

function gatewayLabel(value: string | null): string {
  if (!value) {
    return '';
  }

  if (Object.prototype.hasOwnProperty.call(PAYMENT_SYSTEM_LABELS, value)) {
    return PAYMENT_SYSTEM_LABELS[value as PaymentSystem];
  }

  return value;
}

export function toPaymentsReceivedExportRows(
  payments: readonly PaymentsReceivedRow[],
): PaymentsReceivedExportRow[] {
  return payments.map((row) => ({
    nome: row.studentName || '',
    email: row.studentEmail || '',
    gateway: gatewayLabel(row.paymentSystem),
    valor: formatPaymentAmount(row.value, row.currency),
  }));
}

export function buildPaymentsReceivedCsv(rows: readonly PaymentsReceivedExportRow[]): string {
  const header = ['Nome', 'Email', 'Gateway', 'Valor'];
  const lines = [
    header.join(';'),
    ...rows.map((row) => [
      csvEscape(row.nome),
      csvEscape(row.email),
      csvEscape(row.gateway),
      csvEscape(row.valor),
    ].join(';')),
  ];
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

export function buildPaymentsReceivedJson(rows: readonly PaymentsReceivedExportRow[]): string {
  return `${JSON.stringify(rows, null, 2)}\n`;
}

export function buildPaymentsReceivedPdf(rows: readonly PaymentsReceivedExportRow[]): Uint8Array {
  const title = 'Pagamentos';
  const headers = ['Nome', 'Email', 'Gateway', 'Valor'] as const;
  const colWidths = [160, 180, 80, 80];
  const pageWidth = 595;
  const pageHeight = 842;
  const marginX = 40;
  const marginTop = 50;
  const rowHeight = 16;
  const usableHeight = pageHeight - marginTop - 40;
  const rowsPerPage = Math.max(1, Math.floor((usableHeight - rowHeight) / rowHeight));

  const pages: PaymentsReceivedExportRow[][] = [];
  if (rows.length === 0) {
    pages.push([]);
  } else {
    for (let index = 0; index < rows.length; index += rowsPerPage) {
      pages.push(rows.slice(index, index + rowsPerPage));
    }
  }

  return assemblePdf([
    '<< /Type /Catalog /Pages 2 0 R >>',
    `<< /Type /Pages /Kids [${pages.map((_, index) => `${4 + index * 2} 0 R`).join(' ')}] /Count ${pages.length} >>`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
    ...pages.flatMap((pageRows, pageIndex) => {
      const content = buildPdfPageContent({
        title,
        headers,
        colWidths,
        marginX,
        marginTop,
        rowHeight,
        rows: pageRows,
        pageNumber: pageIndex + 1,
        totalPages: pages.length,
      });
      const pageObjectId = 4 + pageIndex * 2;
      const contentObjectId = pageObjectId + 1;
      return [
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Contents ${contentObjectId} 0 R /Resources << /Font << /F1 3 0 R >> >> >>`,
        `<< /Length ${byteLength(content)} >>\nstream\n${content}\nendstream`,
      ];
    }),
  ]);
}

export function paymentsReceivedExportFileName(
  format: PaymentsReceivedExportFormat,
  dateStart: string,
  dateEnd: string,
): string {
  const range = dateStart === dateEnd ? dateStart : `${dateStart}_${dateEnd}`;
  return `pagamentos_${range}.${format}`;
}

export function downloadTextFile(content: string, fileName: string, mimeType: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadBinaryFile(content: Uint8Array, fileName: string, mimeType: string): void {
  const copy = new Uint8Array(content);
  const url = URL.createObjectURL(new Blob([copy], { type: mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function buildPdfPageContent(input: {
  title: string;
  headers: readonly ['Nome', 'Email', 'Gateway', 'Valor'];
  colWidths: number[];
  marginX: number;
  marginTop: number;
  rowHeight: number;
  rows: readonly PaymentsReceivedExportRow[];
  pageNumber: number;
  totalPages: number;
}): string {
  const lines: string[] = [];
  let y = 842 - input.marginTop;

  lines.push('BT');
  lines.push('/F1 14 Tf');
  lines.push(pdfTextCommand(input.marginX, y, input.title));
  y -= 28;

  lines.push('/F1 10 Tf');
  let x = input.marginX;
  input.headers.forEach((header, index) => {
    lines.push(pdfTextCommand(x, y, header));
    x += input.colWidths[index];
  });
  y -= input.rowHeight;

  lines.push('/F1 9 Tf');
  for (const row of input.rows) {
    const values = [
      truncatePdfText(row.nome, 28),
      truncatePdfText(row.email, 32),
      truncatePdfText(row.gateway, 14),
      truncatePdfText(row.valor, 14),
    ];
    x = input.marginX;
    values.forEach((value, index) => {
      lines.push(pdfTextCommand(x, y, value));
      x += input.colWidths[index];
    });
    y -= input.rowHeight;
  }

  lines.push('/F1 8 Tf');
  lines.push(pdfTextCommand(input.marginX, 24, `Pagina ${input.pageNumber} de ${input.totalPages}`));
  lines.push('ET');
  return lines.join('\n');
}

function pdfTextCommand(x: number, y: number, text: string): string {
  return `1 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)} Tm (${escapePdfString(text)}) Tj`;
}

function truncatePdfText(value: string, maxChars: number): string {
  if (value.length <= maxChars) {
    return value;
  }

  return `${value.slice(0, Math.max(0, maxChars - 3))}...`;
}

function escapePdfString(value: string): string {
  let output = '';
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (char === '\\' || char === '(' || char === ')') {
      output += `\\${char}`;
      continue;
    }

    if (code >= 32 && code <= 126) {
      output += char;
      continue;
    }

    if (code <= 255) {
      output += `\\${code.toString(8).padStart(3, '0')}`;
      continue;
    }

    output += '?';
  }

  return output;
}

function assemblePdf(objectBodies: string[]): Uint8Array {
  const chunks: Uint8Array[] = [];
  const offsets = [0];
  let length = 0;

  const push = (text: string) => {
    const bytes = encodeLatin1(text);
    chunks.push(bytes);
    length += bytes.length;
  };

  push('%PDF-1.4\n');
  objectBodies.forEach((body, index) => {
    offsets.push(length);
    push(`${index + 1} 0 obj\n${body}\nendobj\n`);
  });

  const xrefOffset = length;
  push(`xref\n0 ${objectBodies.length + 1}\n`);
  push('0000000000 65535 f \n');
  for (let index = 1; index <= objectBodies.length; index += 1) {
    push(`${String(offsets[index]).padStart(10, '0')} 00000 n \n`);
  }
  push(`trailer\n<< /Size ${objectBodies.length + 1} /Root 1 0 R >>\n`);
  push(`startxref\n${xrefOffset}\n%%EOF\n`);

  const result = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }

  return result;
}

function byteLength(value: string): number {
  return value.length;
}

function encodeLatin1(value: string): Uint8Array {
  const bytes = new Uint8Array(value.length);
  for (let index = 0; index < value.length; index += 1) {
    bytes[index] = value.charCodeAt(index) & 0xff;
  }
  return bytes;
}

function csvEscape(value: string): string {
  if (/[;"\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
}
